import { useState } from "react";
import { ChevronRight, GripVertical } from "lucide-react";
import { registry } from "@dynamic-landing/components";
import { cn } from "@/lib/cn";
import { findParent } from "@/editor/treeOps";
import type { ComponentNode, ComponentTree } from "@/editor/types";

/**
 * A structural outline (layers panel) of the page tree: click any node to
 * select it, expand/collapse containers, and DRAG rows to reorder among
 * siblings or reparent into a container (capability #9, native HTML5 DnD —
 * no extra dependency). The actual move is applied by `onMove`, which the host
 * wires to layout's `moveNodeToParent` through the undoable tree transform.
 */
type DropPos = "before" | "after" | "inside";
type DropHint = { id: string; pos: DropPos } | null;

function acceptsChildren(name: string): boolean {
  const reg = registry as Record<string, { acceptsChildren?: boolean } | undefined>;
  return !!reg[name]?.acceptsChildren;
}

export function TreeOutline({
  tree,
  selectedNodeId,
  onSelect,
  onMove,
  multiSelected,
  onToggleSelect,
}: {
  tree: ComponentTree | null;
  selectedNodeId: string | null;
  onSelect: (id: string) => void;
  /** Move `nodeId` into `toParentId` at `index` (undefined → append). */
  onMove?: (nodeId: string, toParentId: string, index?: number) => void;
  /** Ids in the multi-selection (⌘/Ctrl-click) — highlighted for batch ops. */
  multiSelected?: Set<string>;
  /** Toggle a node in the multi-selection (⌘/Ctrl-click). */
  onToggleSelect?: (id: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [hint, setHint] = useState<DropHint>(null);

  if (!tree) return null;

  const isDescendant = (ancestorId: string, maybeChildId: string): boolean => {
    // True if maybeChildId lives inside ancestorId's subtree (or is it).
    const walk = (n: ComponentNode): boolean => {
      if (n.node_id === maybeChildId) return true;
      return (n.children ?? []).some(walk);
    };
    const anc = findNode(tree.root, ancestorId);
    return anc ? walk(anc) : false;
  };

  const drop = (targetId: string, pos: DropPos) => {
    setDragId(null);
    setHint(null);
    if (!onMove || !dragId || dragId === targetId) return;
    // Never drop a node into itself or its own subtree.
    if (isDescendant(dragId, targetId)) return;

    if (pos === "inside") {
      onMove(dragId, targetId); // append into the container
      return;
    }
    const parent = findParent(tree, targetId);
    if (!parent) return; // target is the root — can't add a sibling to it
    const siblings = (parent.children ?? []).filter((c) => c.node_id !== dragId);
    const idx = siblings.findIndex((c) => c.node_id === targetId);
    if (idx < 0) return;
    onMove(dragId, parent.node_id, pos === "before" ? idx : idx + 1);
  };

  return (
    <div className="rounded-btn border border-hairline bg-paper-2/40 p-1.5 dark:bg-white/5">
      <p className="eyebrow px-1.5 pb-1 pt-0.5">Outline</p>
      <div className="max-h-72 overflow-y-auto">
        <OutlineNode
          node={tree.root}
          depth={0}
          isRoot
          selectedNodeId={selectedNodeId}
          onSelect={onSelect}
          dndEnabled={!!onMove}
          dragId={dragId}
          hint={hint}
          setDragId={setDragId}
          setHint={setHint}
          onDrop={drop}
          multiSelected={multiSelected}
          onToggleSelect={onToggleSelect}
        />
      </div>
    </div>
  );
}

function findNode(node: ComponentNode, id: string): ComponentNode | null {
  if (node.node_id === id) return node;
  for (const c of node.children ?? []) {
    const hit = findNode(c, id);
    if (hit) return hit;
  }
  return null;
}

function OutlineNode({
  node,
  depth,
  isRoot,
  selectedNodeId,
  onSelect,
  dndEnabled,
  dragId,
  hint,
  setDragId,
  setHint,
  onDrop,
  multiSelected,
  onToggleSelect,
}: {
  node: ComponentNode;
  depth: number;
  isRoot?: boolean;
  selectedNodeId: string | null;
  onSelect: (id: string) => void;
  dndEnabled: boolean;
  dragId: string | null;
  hint: DropHint;
  setDragId: (id: string | null) => void;
  setHint: (h: DropHint) => void;
  onDrop: (targetId: string, pos: DropPos) => void;
  multiSelected?: Set<string>;
  onToggleSelect?: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const kids = node.children ?? [];
  const hasKids = kids.length > 0;
  const inMulti = !!multiSelected?.has(node.node_id);
  const sel = node.node_id === selectedNodeId || inMulti;
  const container = acceptsChildren(node.component.name);
  const dragging = dragId === node.node_id;
  const showHint = hint?.id === node.node_id ? hint.pos : null;
  // The root can't be dragged or used as a before/after sibling target, but it
  // IS a valid "inside" drop target (append to the page).
  const draggable = dndEnabled && !isRoot;

  const computePos = (e: React.DragEvent): DropPos => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    if (isRoot) return "inside";
    if (container) return y < 0.28 ? "before" : y > 0.72 ? "after" : "inside";
    return y < 0.5 ? "before" : "after";
  };

  return (
    <div>
      <div
        draggable={draggable}
        onDragStart={(e) => {
          if (!draggable) return;
          e.dataTransfer.effectAllowed = "move";
          setDragId(node.node_id);
        }}
        onDragEnd={() => {
          setDragId(null);
          setHint(null);
        }}
        onDragOver={(e) => {
          if (!dndEnabled || !dragId || dragId === node.node_id) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          const pos = computePos(e);
          if (hint?.id !== node.node_id || hint.pos !== pos) setHint({ id: node.node_id, pos });
        }}
        onDragLeave={() => {
          if (hint?.id === node.node_id) setHint(null);
        }}
        onDrop={(e) => {
          if (!dndEnabled || !dragId) return;
          e.preventDefault();
          onDrop(node.node_id, computePos(e));
        }}
        className={cn(
          "group relative flex items-center gap-1 rounded text-[12.5px] transition-colors",
          sel ? "bg-accent-soft text-accent" : "text-ink hover:bg-paper-2 dark:hover:bg-white/5",
          dragging && "opacity-40",
          showHint === "inside" && "ring-1 ring-inset ring-accent",
        )}
        style={{ paddingLeft: depth * 12 + 4 }}
      >
        {/* sibling drop indicators */}
        {showHint === "before" && <span className="absolute inset-x-1 top-0 h-0.5 rounded bg-accent" />}
        {showHint === "after" && <span className="absolute inset-x-1 bottom-0 h-0.5 rounded bg-accent" />}

        {draggable && (
          <span
            className="grid h-5 w-3.5 shrink-0 cursor-grab place-items-center text-subtle opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing"
            aria-hidden
          >
            <GripVertical className="h-3 w-3" strokeWidth={2} />
          </span>
        )}
        {hasKids ? (
          <button
            type="button"
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => setOpen((o) => !o)}
            className="grid h-5 w-4 shrink-0 place-items-center text-subtle"
          >
            <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} strokeWidth={2.25} />
          </button>
        ) : (
          <span className="h-5 w-4 shrink-0" aria-hidden />
        )}
        <button
          type="button"
          onClick={(e) => {
            if ((e.metaKey || e.ctrlKey) && onToggleSelect && !isRoot) onToggleSelect(node.node_id);
            else onSelect(node.node_id);
          }}
          className="min-w-0 flex-1 truncate py-1 pr-1 text-left"
          title={`${node.node_id}${isRoot ? "" : " · ⌘-click to multi-select"}`}
        >
          {inMulti && <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-accent align-middle" />}
          {node.component.name}
          {hasKids ? <span className="ml-1 text-subtle">· {kids.length}</span> : null}
        </button>
      </div>
      {hasKids && open
        ? kids.map((c) => (
            <OutlineNode
              key={c.node_id}
              node={c}
              depth={depth + 1}
              selectedNodeId={selectedNodeId}
              onSelect={onSelect}
              dndEnabled={dndEnabled}
              dragId={dragId}
              hint={hint}
              setDragId={setDragId}
              setHint={setHint}
              onDrop={onDrop}
              multiSelected={multiSelected}
              onToggleSelect={onToggleSelect}
            />
          ))
        : null}
    </div>
  );
}
